import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import { printOwnerStatement } from '../utils/printOwnerStatement';

export default function OwnerSettlement() {
  const { authFetch, systemSettings, user } = useContext(AppContext);
  const { t } = useTranslation();
  
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  
  const [data, setData] = useState({ ledger: [], summary: {}, closures: [], stock_inventory: [] });
  const [loading, setLoading] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  useEffect(() => {
    fetchData();
  }, [selectedMonth, authFetch]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`/api/owner_settlement?action=ledger&month=${selectedMonth}`);
      
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'CFA' }).format(val || 0).replace('CFA', '').trim();

  const summary = data.summary || {};
  const totalSales = parseFloat(summary.total_sales_revenue || 0);
  const cogs = parseFloat(summary.total_cogs || 0);
  const wastes = parseFloat(summary.wastes || 0);
  const initialDebt = parseFloat(summary.initial_debt || 0);
  const totalBranchesCost = parseFloat(summary.total_branches_cost || 0);
  const totalBranchesSellValue = parseFloat(summary.total_branches_sell_value || 0);
  
  // Monthly Net Profit: Sales - COGS - Wastes
  const monthlyNetProfit = parseFloat(summary.net_profit || 0);

  // Outstanding debt balance at the end of this period:
  // Base capital (Initial capital or total branches stock cost) - COGS repaid - Wastes
  const baseCapital = initialDebt > 0 ? initialDebt : totalBranchesCost;
  const remainingDebt = baseCapital - cogs - wastes;
  const totalOwnerEquity = totalBranchesCost + monthlyNetProfit;

  // Format month name for display (e.g., "OCTOBER 2026")
  const displayMonth = new Date(`${selectedMonth}-01`).toLocaleString('en-US', { month: 'long', year: 'numeric' }).toUpperCase();

  const stockInventory = data.stock_inventory || [];

  const filteredStockInventory = stockInventory.filter(item => {
    const q = productSearch.toLowerCase();
    return !productSearch || 
      (item.product_name && item.product_name.toLowerCase().includes(q)) ||
      (item.sku && item.sku.toLowerCase().includes(q)) ||
      (item.depo_name && item.depo_name.toLowerCase().includes(q));
  });

  // Prints the official company-letterhead statement
  const handlePrint = () => {
    printOwnerStatement({
      settings: systemSettings || {},
      preparedBy: user?.full_name || user?.name || user?.username || '',
      selectedMonth,
      displayMonth,
      figures: {
        totalBranchesCost,
        totalBranchesSellValue,
        initialDebt,
        totalSales,
        cogs,
        wastes,
        remainingDebt,
        monthlyNetProfit,
        totalOwnerEquity,
      },
      stockInventory,
    });
  };

  return (
    <div lang="en" className="min-h-screen bg-slate-50 p-6 md:p-8 font-sans antialiased text-slate-900" style={{ fontFamily: '"Inter", sans-serif' }}>
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4 pb-2 border-b border-slate-200">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                Finance & Consignment
              </span>
              <span className="text-xs text-slate-400 font-medium">•</span>
              <span className="text-xs font-medium text-slate-500">{displayMonth}</span>
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mt-1">{t('Owner Settlement & Stock Finance')}</h1>
            <p className="text-slate-500 text-sm font-medium mt-1">
              {t('Capital investment tracking, stock valuations, revenue, COGS, and owner profit settlement.')}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button 
              onClick={handlePrint}
              className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 rounded-xl font-medium text-sm transition-all shadow-sm flex items-center gap-2 cursor-pointer active:scale-95"
            >
              <svg className="w-4 h-4 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              {t('Print Statement')}
            </button>

            <div className="bg-white border border-slate-200 shadow-sm rounded-xl px-4 py-2 flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('Period')}:</span>
              <input 
                type="month" 
                value={selectedMonth} 
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="text-slate-900 font-semibold text-sm border-none bg-transparent outline-none cursor-pointer"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 space-y-3">
             <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-300 border-t-slate-900"></div>
             <p className="text-xs text-slate-500 font-medium">{t('Calculating stock valuations and financial balances...')}</p>
          </div>
        ) : (
          <>
            {/* Top KPI Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              
              {/* Card 1: Total Stock Cost */}
              <div className="bg-white rounded-xl shadow-xs border border-slate-200 p-4 hover:border-slate-300 transition-colors">
                <div className="flex items-center justify-between text-slate-500 mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wider">{t('Stock Cost (Capital)')}</span>
                  <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                </div>
                <p className="text-xl font-bold text-slate-900 tracking-tight">{formatCurrency(totalBranchesCost)} <span className="text-xs font-normal text-slate-400">CFA</span></p>
                <div className="mt-2 text-xs text-slate-500 flex items-center justify-between">
                  <span>Retail Worth:</span>
                  <span className="font-semibold text-blue-600">{formatCurrency(totalBranchesSellValue)} CFA</span>
                </div>
              </div>

              {/* Card 2: Monthly Sales */}
              <div className="bg-white rounded-xl shadow-xs border border-slate-200 p-4 hover:border-slate-300 transition-colors">
                <div className="flex items-center justify-between text-slate-500 mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wider">{t('Total Sales')}</span>
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                </div>
                <p className="text-xl font-bold text-slate-900 tracking-tight">{formatCurrency(totalSales)} <span className="text-xs font-normal text-slate-400">CFA</span></p>
                <div className="mt-2 text-xs text-slate-500 flex items-center justify-between">
                  <span>COGS Settled:</span>
                  <span className="font-semibold text-emerald-600">-{formatCurrency(cogs)} CFA</span>
                </div>
              </div>

              {/* Card 3: Monthly Net Profit */}
              <div className="bg-purple-50/60 rounded-xl shadow-xs border border-purple-200/80 p-4 hover:border-purple-300 transition-colors">
                <div className="flex items-center justify-between text-purple-700 mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wider">{t('Net Profit')}</span>
                  <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                </div>
                <p className="text-xl font-bold text-purple-950 tracking-tight">{formatCurrency(monthlyNetProfit)} <span className="text-xs font-normal text-purple-600">CFA</span></p>
                <div className="mt-2 text-xs text-purple-700 flex items-center justify-between">
                  <span>Margin:</span>
                  <span className="font-semibold">{totalSales > 0 ? ((monthlyNetProfit / totalSales) * 100).toFixed(1) : 0}%</span>
                </div>
              </div>

              {/* Card 4: Remaining Supermarket Debt */}
              <div className="bg-slate-900 rounded-xl shadow-xs border border-slate-800 p-4 text-white">
                <div className="flex items-center justify-between text-slate-400 mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wider">{t('Remaining Debt')}</span>
                  <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                </div>
                <p className="text-xl font-bold text-white tracking-tight">{formatCurrency(remainingDebt)} <span className="text-xs font-normal text-slate-400">CFA</span></p>
                <p className="mt-2 text-[11px] font-medium text-slate-400 leading-tight">
                  {t('Supermarket debt carried to owner')}
                </p>
              </div>

            </div>

            {/* Financial Ledger & Consignment Table */}
            <div className="bg-white rounded-xl shadow-xs border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 bg-slate-50/70 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <h2 className="text-base font-bold text-slate-900">{t('Consignment Capital & Debt Settlement Statement')}</h2>
                  <p className="text-xs text-slate-500 mt-0.5">{t('Supermarket financial balance equation incorporating stock capital, sales, COGS, and profit.')}</p>
                </div>
                <span className="text-xs font-semibold text-slate-500 bg-white border border-slate-200 px-3 py-1 rounded-md self-start sm:self-auto">
                  {t('Month')}: {displayMonth}
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-slate-50/50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3.5 text-xs font-bold text-slate-600 uppercase tracking-wider">{t('Item Description')}</th>
                      <th className="px-6 py-3.5 text-xs font-bold text-slate-600 uppercase tracking-wider text-right">{t('Financial Amount (CFA)')}</th>
                      <th className="px-6 py-3.5 text-xs font-bold text-slate-600 uppercase tracking-wider">{t('Details & Accounting Notes')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    
                    {/* Row 1: Total Branches Cost */}
                    <tr className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4 font-semibold text-slate-800 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                        {t('Total Branches Cost (Capital Invested)')}
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-slate-900">{formatCurrency(totalBranchesCost)} CFA</td>
                      <td className="px-6 py-4 text-slate-500 text-xs">{t('Owner total capital across all branches (Stock at purchase cost)')}</td>
                    </tr>

                    {/* Row 2: Stock Retail Valuation */}
                    <tr className="hover:bg-slate-50/50 transition-colors bg-blue-50/20">
                      <td className="px-6 py-4 font-semibold text-blue-900 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                        {t('Total Stock Retail Valuation (Market Selling Value)')}
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-blue-700">{formatCurrency(totalBranchesSellValue)} CFA</td>
                      <td className="px-6 py-4 text-blue-600/80 text-xs font-medium">
                        {t('Projected selling worth: Potential retail gain')} +{formatCurrency(totalBranchesSellValue - totalBranchesCost)} CFA
                      </td>
                    </tr>

                    {/* Row 3: Initial Consignment Capital */}
                    <tr className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4 font-medium text-slate-700">{t('Original Consignment Capital (Initial Debt)')}</td>
                      <td className="px-6 py-4 text-right font-semibold text-slate-800">{formatCurrency(initialDebt)} CFA</td>
                      <td className="px-6 py-4 text-slate-500 text-xs">{t('Initial debt carried forward prior to this month')}</td>
                    </tr>

                    {/* Row 4: Monthly Revenue */}
                    <tr className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4 font-medium text-slate-700">{t('Monthly Revenue (Total Sales)')}</td>
                      <td className="px-6 py-4 text-right font-bold text-blue-600">+{formatCurrency(totalSales)} CFA</td>
                      <td className="px-6 py-4 text-slate-500 text-xs">{t('Total sales revenue collected in this month')}</td>
                    </tr>

                    {/* Row 5: Monthly COGS */}
                    <tr className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4 font-medium text-slate-700">{t('Monthly COGS (Cost of Sales Settled)')}</td>
                      <td className="px-6 py-4 text-right font-bold text-emerald-600">-{formatCurrency(cogs)} CFA</td>
                      <td className="px-6 py-4 text-slate-500 text-xs">{t('Cost of goods sold deducted from inventory')}</td>
                    </tr>

                    {/* Row 6: Wastes */}
                    <tr className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4 font-medium text-slate-700">{t('Wastes & Damaged Goods Deducted')}</td>
                      <td className="px-6 py-4 text-right font-bold text-rose-500">-{formatCurrency(wastes)} CFA</td>
                      <td className="px-6 py-4 text-slate-500 text-xs">{t('Documented losses from damaged or expired items')}</td>
                    </tr>

                    {/* Row 7: Remaining Supermarket Debt (Carried Forward) */}
                    <tr className="bg-slate-100/80 hover:bg-slate-100 transition-colors border-y-2 border-slate-300 font-bold">
                      <td className="px-6 py-4 text-slate-900 text-base">{t('Remaining Supermarket Debt')}</td>
                      <td className="px-6 py-4 text-right font-extrabold text-slate-900 text-lg">{formatCurrency(remainingDebt)} CFA</td>
                      <td className="px-6 py-4 text-slate-600 text-xs uppercase tracking-wider font-semibold">
                        {t('CARRIED FORWARD (Base Capital - COGS - Wastes)')}
                      </td>
                    </tr>

                    {/* Row 8: Monthly Net Profit */}
                    <tr className="bg-purple-50/70 hover:bg-purple-100/70 transition-colors border-b border-purple-200 font-bold">
                      <td className="px-6 py-4 text-purple-950 text-base">{t('Monthly Net Profit (All Branches)')}</td>
                      <td className="px-6 py-4 text-right font-extrabold text-purple-700 text-lg">{formatCurrency(monthlyNetProfit)} CFA</td>
                      <td className="px-6 py-4 text-purple-800 text-xs font-semibold uppercase tracking-wider">
                        {t('SALES - COGS - WASTES')}
                      </td>
                    </tr>

                    {/* Row 9: Total Owner Returns */}
                    <tr className="bg-amber-50/80 hover:bg-amber-100/80 transition-colors border-b-2 border-amber-300 font-bold">
                      <td className="px-6 py-4 text-amber-950 text-base">{t('Total Owner Returns (Capital + Profit)')}</td>
                      <td className="px-6 py-4 text-right font-black text-amber-800 text-xl">{formatCurrency(totalOwnerEquity)} CFA</td>
                      <td className="px-6 py-4 text-amber-900 text-xs font-bold uppercase tracking-wide">
                        {t('TOTAL COMPREHENSIVE SETTLEMENT')}
                      </td>
                    </tr>

                  </tbody>
                </table>
              </div>
            </div>

            {/* Stock Valuation Section */}
            <div className="bg-white rounded-xl shadow-xs border border-slate-200 overflow-hidden">
              
              {/* Header and Search Bar */}
              <div className="px-6 py-4 border-b border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                
                <div className="flex items-center gap-3">
                  <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                    <span>🏢</span>
                    <span>{t('Stock & Branches Valuation')}</span>
                  </h3>
                  <span className="bg-slate-900 text-white px-2.5 py-0.5 rounded-full text-xs font-bold">
                    {stockInventory.length} {t('Items')}
                  </span>
                </div>

                {/* Filter and Search */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder={t('Search products, SKU, branch...')}
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="w-56 sm:w-72 pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-900"
                  />
                  <svg className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {productSearch && (
                    <button
                      onClick={() => setProductSearch('')}
                      className="absolute right-2 top-2 text-slate-400 hover:text-slate-600 text-xs"
                    >
                      ×
                    </button>
                  )}
                </div>

              </div>

              {/* Stock Inventory Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-slate-50/70 border-b border-slate-200">
                    <tr>
                      <th className="px-5 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">{t('Product & SKU')}</th>
                      <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">{t('Branch / Warehouse')}</th>
                      <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">{t('Stock Qty')}</th>
                      <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">{t('Unit Cost')}</th>
                      <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">{t('Unit Sell Price')}</th>
                      <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">{t('Total Stock Cost (Capital)')}</th>
                      <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">{t('Stock Retail Value')}</th>
                      <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">{t('Projected Gain')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-xs">
                    {filteredStockInventory.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-12 text-center text-slate-400 font-medium">
                          {t('No stock inventory matching search.')}
                        </td>
                      </tr>
                    ) : (
                      filteredStockInventory.map((item) => {
                        const cost = parseFloat(item.total_cost || 0);
                        const sell = parseFloat(item.total_sell_value || 0);
                        const margin = parseFloat(item.projected_margin || 0);

                        return (
                          <tr key={item.id} className="hover:bg-slate-50/60 transition-colors">
                            <td className="px-5 py-3.5">
                              <div className="font-bold text-slate-900 text-sm">{item.product_name}</div>
                              <div className="text-[11px] text-slate-400 font-mono">{item.sku || 'No SKU'}</div>
                            </td>
                            <td className="px-4 py-3.5 font-medium text-slate-700">
                              {item.depo_name || '-'}
                            </td>
                            <td className="px-4 py-3.5 text-right font-extrabold text-slate-900">
                              <span className="bg-slate-100 px-2.5 py-1 rounded-md text-xs font-mono">
                                {parseFloat(item.quantity).toLocaleString()}
                              </span>
                            </td>
                            <td className="px-4 py-3.5 text-right font-medium text-slate-600">
                              {formatCurrency(item.unit_cost)} CFA
                            </td>
                            <td className="px-4 py-3.5 text-right font-semibold text-slate-800">
                              {formatCurrency(item.unit_price)} CFA
                            </td>
                            <td className="px-4 py-3.5 text-right font-bold text-slate-900">
                              {formatCurrency(cost)} CFA
                            </td>
                            <td className="px-4 py-3.5 text-right font-bold text-blue-600">
                              {formatCurrency(sell)} CFA
                            </td>
                            <td className="px-4 py-3.5 text-right font-bold text-emerald-600">
                              +{formatCurrency(margin)} CFA
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  {filteredStockInventory.length > 0 && (
                    <tfoot className="bg-slate-50 border-t-2 border-slate-200 font-bold text-xs text-slate-800">
                      <tr>
                        <td colSpan={2} className="px-5 py-3.5 font-bold uppercase tracking-wider text-slate-600">
                          {t('Total Branches Stock Valuation')} ({filteredStockInventory.length} {t('Items')})
                        </td>
                        <td className="px-4 py-3.5 text-right font-black text-slate-900">
                          {filteredStockInventory.reduce((sum, p) => sum + parseFloat(p.quantity || 0), 0).toLocaleString()}
                        </td>
                        <td colSpan={2}></td>
                        <td className="px-4 py-3.5 text-right font-black text-slate-900">
                          {formatCurrency(totalBranchesCost)} CFA
                        </td>
                        <td className="px-4 py-3.5 text-right font-black text-blue-600">
                          {formatCurrency(totalBranchesSellValue)} CFA
                        </td>
                        <td className="px-4 py-3.5 text-right font-black text-emerald-600">
                          +{formatCurrency(totalBranchesSellValue - totalBranchesCost)} CFA
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

            </div>
          </>
        )}
      </div>
    </div>
  );
}
